<template>
    <BaseDialog
        v-model:open="open"
        :title="t('developmentJourneys.actionFeedbackDialog.title')"
        :description="t('developmentJourneys.actionFeedbackDialog.description')"
        :show-close="!isSubmitting"
        :disable-close-on-backdrop-click="isSubmitting">
        <form :id="formId" data-testid="development-action-feedback-dialog" class="flex flex-col gap-4" @submit.prevent="submit">
            <p class="body-small text-ink-muted">{{ actionTitle }}</p>

            <BaseRadioGroup
                v-model="outcome"
                :options="outcomeOptions"
                :label="t('developmentJourneys.actionFeedbackDialog.chooseOutcome')"
                appearance="cards"
                orientation="horizontal"
                :disabled="isSubmitting" />

            <label for="development-action-feedback-learning" class="body-small-semibold flex flex-col gap-2 text-control-label">
                <span
                    >{{ t("developmentJourneys.actionFeedbackDialog.learningLabel") }}
                    <span class="body-small text-ink-muted">{{ t("developmentJourneys.actionFeedbackDialog.optionalSuffix") }}</span></span
                >
                <BaseTextarea
                    id="development-action-feedback-learning"
                    v-model:input="whatLearned"
                    data-testid="development-action-feedback-learning"
                    :aria-label="t('developmentJourneys.actionFeedbackDialog.learningLabel')"
                    :maxlength="FEEDBACK_TEXT_MAX_LENGTH"
                    :disabled="isSubmitting"
                    disable-autosize
                    class="min-h-32 resize-y rounded-lg border border-control-border bg-control-bg p-3 text-control-text transition-colors"
                    :class="tonalThemeEnabled ? 'focus-field' : 'focus-ring'" />
            </label>

            <div role="alert" aria-atomic="true">
                <BaseNotification v-if="errorMessage" role="none" theme="error" :text="errorMessage" hide-dismiss />
            </div>
        </form>
        <template #footer>
            <JourneyButton theme="tertiary" size="md" :disabled="isSubmitting" @click="open = false">{{ t("developmentJourneys.actionFeedbackDialog.skip") }}</JourneyButton>
            <JourneyButton :form="formId" type="submit" theme="primary" size="md" :disabled="!canSubmit" :aria-busy="isSubmitting">
                {{ isSubmitting ? t("developmentJourneys.actionFeedbackDialog.submitting") : t("developmentJourneys.actionFeedbackDialog.submitReview") }}
            </JourneyButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import type { DevelopmentActionFeedbackOutcome, DevelopmentActionFeedbackPayload } from "~vue/api/generated/feedback";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BaseRadioGroup from "~vue/components/form/BaseRadioGroup.vue";
import BaseTextarea from "~vue/components/form/BaseTextarea.vue";
import JourneyButton from "~vue/pages/developmentJourneys/DevelopmentJourneyButton.vue";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, ref, useId, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const FEEDBACK_TEXT_MAX_LENGTH = 2_000;
const formId = useId();
// Locale catalogue can finish loading after mount, so option labels must stay reactive to the active locale.
const outcomeOptions = computed<{ value: DevelopmentActionFeedbackOutcome; label: string }[]>(() => [
    { value: "good", label: t("developmentJourneys.actionFeedbackDialog.outcomeGood") },
    { value: "okay", label: t("developmentJourneys.actionFeedbackDialog.outcomeOkay") },
    { value: "bad", label: t("developmentJourneys.actionFeedbackDialog.outcomeBad") },
]);

const props = defineProps<{
    actionTitle: string;
    isSubmitting: boolean;
    errorMessage: string | null;
}>();
const emit = defineEmits<{
    submit: [payload: DevelopmentActionFeedbackPayload];
}>();
const open = defineModel<boolean>("open", { default: false });
const outcome = ref<DevelopmentActionFeedbackOutcome | null>(null);
const whatLearned = ref("");
const canSubmit = computed(() => !props.isSubmitting && outcome.value !== null);

const submit = () => {
    if (!canSubmit.value || outcome.value === null) {
        return;
    }
    const learning = whatLearned.value.trim();
    emit("submit", {
        outcome: outcome.value,
        what_learned: learning || null,
    });
};

watch(open, (isOpen) => {
    if (!isOpen) {
        outcome.value = null;
        whatLearned.value = "";
    }
});
</script>
